import { toMutation } from 'common/util/queryHelper';
import { mutateGraphQL } from 'common/util/query';
import { downloadPDFFromBuffer } from 'common/util/pdfDownloader';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';

export const EXPORT_DOA_V2_BACKEND_START = 'EXPORT_DOA_V2_BACKEND_START';
export const EXPORT_DOA_V2_BACKEND_SUCCESS = 'EXPORT_DOA_V2_BACKEND_SUCCESS';
export const EXPORT_DOA_V2_BACKEND_ERROR = 'EXPORT_DOA_V2_BACKEND_ERROR';

/**
 * Export DOA PDF using backend generation
 * @param {string} institutionId - Institution ID
 * @param {string} binderId - Binder ID
 * @param {string} studyId - Study ID (optional)
 * @returns {Function} Redux thunk action
 */
export function exportDOAV2Backend(institutionId, binderId, studyId) {
    return (dispatch) => {
        dispatch({ type: EXPORT_DOA_V2_BACKEND_START });

        // Get timezone information (not used in mutation, but kept for potential future use)
        const timezoneOffset = new Date().getTimezoneOffset() * -1; // Convert to minutes from UTC
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const mutation = toMutation({
            name: 'generateDOAExportV2',
            args: {
                institutionId: institutionId,
                binderId: binderId,
                studyId: studyId || null,
                timezoneOffset: timezoneOffset,  // Add timezone offset
                timezone: timezone,              // Add timezone
            },
            fields: [
                'buffer',
                'filename',
                'mimeType'
            ]
        });

        return mutateGraphQL(mutation)
            .then((response) => {
                // Check for GraphQL errors first
                if (response.errors && response.errors.length > 0) {
                    const errorMessage = response.errors.map(err => err.message).join(', ');
                    console.error('GraphQL Errors:', response.errors);
                    throw new Error(`GraphQL Error: ${errorMessage}`);
                }

                // Check if response has data and the expected mutation result
                // Handle both response.data.generateDOAExportV2 and response.generateDOAExportV2 patterns
                const mutationResult = response.data.generateDOAExportV2 || response.generateDOAExportV2;

                if (!mutationResult) {
                    console.error('Invalid response structure:', response);
                    throw new Error('No data received from server. Please check if the DOA export service is working properly.');
                }

                const { buffer, filename, mimeType } = mutationResult;

                // Validate required fields
                if (!buffer || !filename || !mimeType) {
                    console.error('Missing fields in response:', { buffer: !!buffer, filename, mimeType });
                    throw new Error('Incomplete response received from server. Missing required fields.');
                }

                // Download the PDF
                try {
                    downloadPDFFromBuffer(buffer, filename, mimeType);

                    dispatch({
                        type: EXPORT_DOA_V2_BACKEND_SUCCESS,
                        payload: { filename },
                    });

                    dispatch(addApplicationMessage('success', `Successfully exported ${filename}`));
                } catch (downloadError) {
                    console.error('Error downloading PDF:', downloadError);
                    dispatch({
                        type: EXPORT_DOA_V2_BACKEND_ERROR,
                        payload: downloadError,
                    });
                    dispatch(addApplicationMessage({
                        message: 'Failed to download PDF. Please try again.',
                        type: 'error',
                    }));
                }
            })
            .catch((error) => {
                console.error('Error generating DOA export:', error);
                dispatch({
                    type: EXPORT_DOA_V2_BACKEND_ERROR,
                    payload: error,
                });
                dispatch(addApplicationMessage({
                    message: 'Failed to generate DOA export. Please try again.',
                    type: 'error',
                }));
                throw error;
            });
    };
}

/**
 * Action creators for reducer
 */
export const exportDOAV2BackendStart = () => ({
    type: EXPORT_DOA_V2_BACKEND_START,
});

export const exportDOAV2BackendSuccess = (filename) => ({
    type: EXPORT_DOA_V2_BACKEND_SUCCESS,
    payload: { filename },
});

export const exportDOAV2BackendError = (error) => ({
    type: EXPORT_DOA_V2_BACKEND_ERROR,
    payload: error,
});
